import React, { useEffect, useState } from "react";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { useTranslation } from "../localization/useTranslation.ts";
import MentionInput from "../common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "../common/Common.types.ts";
import { AnnualWheelActivitySubTask } from "./AnnualWheelSubTasks.types.ts";
import { useAnnualWheelSubTasksStyles } from "./AnnualWheelSubTasks.styles";
import { Guid } from "../common/guid.ts";
import DotLegalList from "@common/components/dotLegalList/DotLegalList";
import DotLegalListRow from "@common/components/dotLegalList/DotLegalListRow";
import { revealOnRowHoverAttribute } from "@common/components/dotLegalList/DotLegalList.styles";

export interface IAnnualWheelSubTasksProps {
    annualWheelActivityId?: string;
    subTasks: Array<AnnualWheelActivitySubTask>;
    onChange: (subTasks: Array<AnnualWheelActivitySubTask>) => void;
    allowMentions?: boolean;
    readOnly?: boolean;
}

function AnnualWheelSubTasks(props: IAnnualWheelSubTasksProps) {
    const { translateString } = useTranslation();
    const styles = useAnnualWheelSubTasksStyles();
    const [focusOnLastSubTask, setFocusOnLastSubTask] = useState(false);

    const handleDragEnd = (result: DropResult) => {
        setFocusOnLastSubTask(false);
        (document.activeElement as HTMLElement)?.blur();

        if (!result.destination) {
            return;
        }
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) {
            return;
        }

        const newSubTasks = [...props.subTasks];
        const [removed] = newSubTasks.splice(sourceIndex, 1);
        newSubTasks.splice(destinationIndex, 0, removed);
        props.onChange(newSubTasks);
    };

    useEffect(() => {
        if (focusOnLastSubTask && props.allowMentions !== true) {
            const elementToFocus = document.getElementById("subtask-" + (props.subTasks.length - 1).toString());
            elementToFocus?.focus();
            setFocusOnLastSubTask(false);
        }
        // eslint-disable-next-line
    }, [props.subTasks, focusOnLastSubTask]);

    function addSubTask() {
        setFocusOnLastSubTask(true);
        props.onChange([...props.subTasks, { name: "", relations: [] }]);
    }

    function updateSubTask(index: number, subTask: AnnualWheelActivitySubTask) {
        const subTasks = [...props.subTasks];
        subTasks[index] = subTask;
        props.onChange(subTasks);
    }

    function removeSubTask(index: number) {
        props.onChange(props.subTasks.filter((_, subTaskIndex) => subTaskIndex !== index));
    }

    if (props.readOnly && props.subTasks.length === 0) {
        return null;
    }

    return (
        <DotLegalList addButtonText={props.readOnly ? undefined : translateString("addSubTask")} onAddClick={addSubTask}>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="subtasks" isDropDisabled={props.readOnly}>
                    {(provided) => (
                        <Box ref={provided.innerRef} {...provided.droppableProps}>
                            {props.subTasks.map((subTask, index) => {
                                const isLastSubTask = index === props.subTasks.length - 1;
                                return (
                                    <Draggable key={index} draggableId={index.toString()} index={index} isDragDisabled={props.readOnly}>
                                        {(provided, snapshot) => (
                                            <DotLegalListRow
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                sx={[styles.row, snapshot.isDragging && styles.draggingRow]}
                                                startContent={
                                                    <Box
                                                        {...provided.dragHandleProps}
                                                        {...{ [revealOnRowHoverAttribute]: true }}
                                                        sx={[styles.dragHandle, !!props.readOnly && styles.hidden]}
                                                    >
                                                        <DragIndicatorIcon sx={styles.dragIcon} />
                                                    </Box>
                                                }
                                                onRemove={props.readOnly ? undefined : () => removeSubTask(index)}
                                            >
                                                {props.allowMentions ? (
                                                    <MentionInput
                                                        multiline
                                                        mentionSource={{
                                                            entityFromId: props.annualWheelActivityId ?? "",
                                                            entityFromType: EntityType.AnnualWheelActivity,
                                                        }}
                                                        noMargin
                                                        variant="standard"
                                                        key={index}
                                                        placeholder={translateString("subTaskPlaceHolder")}
                                                        debounce={false}
                                                        disabled={props.readOnly}
                                                        onChange={(value, relations) =>
                                                            updateSubTask(index, {
                                                                name: value,
                                                                relations: relations.map((relation) => ({ ...relation, relationId: Guid.newGuid() })),
                                                            })
                                                        }
                                                        value={subTask.name}
                                                        initialTextFieldFocus={focusOnLastSubTask && isLastSubTask}
                                                        manageRelationsExternal
                                                    />
                                                ) : (
                                                    <DotLegalTextField
                                                        noMargin
                                                        variant="standard"
                                                        key={index}
                                                        id={"subtask-" + index.toString()}
                                                        placeholder={translateString("subTaskPlaceHolder")}
                                                        debounce={false}
                                                        disabled={props.readOnly}
                                                        label=""
                                                        onChange={(value) => updateSubTask(index, { name: value, relations: [] })}
                                                        value={subTask.name}
                                                    />
                                                )}
                                            </DotLegalListRow>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        </DotLegalList>
    );
}

export default AnnualWheelSubTasks;
